import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AutoCareCta from "../components/AutoCareCta";
import { useAuth } from "../context/AuthContext";
import LocalLeadForm from "../components/LocalLeadForm";
import SharePhotoPicker from "../components/SharePhotoPicker";
import HostShareKit from "../components/HostShareKit";
import ListingReel from "../components/ListingReel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import DocumentUploader from "../components/DocumentUploader";
import TrustBadges from "../components/TrustBadges";
import FinanceHint from "../components/FinanceHint";
import { KitUpsell } from "../components/KitUpsell";
import { Star, Users, Fuel, Zap, MapPin, Gauge, Calendar, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function CarDetail() {
  const { carId } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [car, setCar] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [licensePath, setLicensePath] = useState(null);
  const [insurancePath, setInsurancePath] = useState(null);
  const [booking, setBooking] = useState(false);
  const [applyCredit, setApplyCredit] = useState(true);
  const [kit, setKit] = useState(null);
  const [kitOn, setKitOn] = useState(false);
  const [kitShip, setKitShip] = useState({ name: user?.name || "", line1: "", city: "", state: "FL", postal_code: "" });
  const credit = Number(user?.credit_balance || 0);

  useEffect(() => {
    api.get(`/cars/${carId}`).then(({ data }) => setCar(data)).catch(() => nav("/browse"));
  }, [carId, nav]);

  if (!car) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const days = start && end ? Math.max(0, (new Date(end) - new Date(start)) / 86400000) : 0;
  const subtotal = days * car.daily_price;
  const fee = subtotal * 0.1;
  const total = subtotal + fee;
  const creditUsed = applyCredit ? Math.min(credit, total) : 0;
  const kitPrice = kitOn && kit ? Number(kit.price) : 0;
  const due = Math.max(0, total - creditUsed) + kitPrice;

  const book = async () => {
    if (!user) { nav("/login"); return; }
    if (!start || !end || days <= 0) { toast.error("Pick valid dates"); return; }
    if (kitOn && (!kitShip.name || !kitShip.line1 || !kitShip.city || !kitShip.state || !kitShip.postal_code)) { toast.error("Add a shipping address for your kit"); return; }
    setBooking(true);
    try {
      const { data: b } = await api.post("/bookings", {
        car_id: car.car_id, start_date: start, end_date: end,
        license_doc_path: licensePath, insurance_doc_path: insurancePath,
      });
      // Fire-and-forget AI doc verification (Claude vision) if both uploaded
      if (licensePath && insurancePath) {
        api.post(`/bookings/${b.booking_id}/verify-docs`).then(({ data }) => {
          if (data.status === "verified") toast.success("Documents verified by AI");
          else toast.warning("Documents flagged for manual review", { description: (data.reasons || []).join(" · ") });
        }).catch(() => {});
      }
      const { data: co } = await api.post("/payments/checkout", { booking_id: b.booking_id, origin_url: window.location.origin, apply_credit: applyCredit && credit > 0, kit_product_id: kitOn && kit ? kit.product_id : undefined, kit_shipping: kitOn && kit ? kitShip : undefined });
      if (co.paid_with_credit) { await refresh?.(); nav(`/payment/success?booking_id=${b.booking_id}&credit=1`); return; }
      window.location.href = co.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
      setBooking(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="car-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative aspect-[16/10] rounded-3xl overflow-hidden bg-slate-900 rise">
            {(car.photos?.[car.share_photo ?? 0] || car.photos?.[0]) && <img src={car.photos[car.share_photo ?? 0] || car.photos[0]} alt={car.model} className="w-full h-full object-cover" />}
            <div className="absolute top-4 left-4 flex gap-2">
              {car.gig_ready && <Badge className="bg-emerald-500/90 text-slate-950 font-mono text-xs uppercase tracking-wider"><Zap className="w-3 h-3 mr-1" />Gig Ready</Badge>}
            </div>
          </div>
          {user?.id === car.owner_id && <SharePhotoPicker car={car} onSaved={(i) => setCar((c) => ({ ...c, share_photo: i }))} />}
          <ListingReel car={car} isOwner={user?.id === car.owner_id} />
          {user?.id === car.owner_id && <HostShareKit carId={car.car_id} />}

          <div className="rise rise-1">
            <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-2">{car.category}</p>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">{car.year} {car.make} {car.model}</h1>
            <p className="text-slate-400 mt-1">{car.trim}</p>
            <div className="flex flex-wrap items-center gap-5 mt-4 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />{car.rating_avg || "New"} <span className="text-slate-500">({car.rating_count} reviews)</span></span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-400" />{car.location}, {car.city}</span>
              <span className="text-slate-500">·</span>
              <span className="text-slate-400">{car.trips_count} trips</span>
            </div>
            <TrustBadges car={car} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rise rise-2">
            {[
              { icon: Users, label: "Seats", val: car.seats },
              { icon: Fuel, label: "Fuel", val: car.fuel },
              { icon: Gauge, label: "Trans.", val: car.transmission },
              { icon: Calendar, label: "Mileage", val: car.mileage ? `${car.mileage.toLocaleString()} mi` : "—" },
            ].map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#131722] border border-slate-800">
                <s.icon className="w-4 h-4 text-cyan-400 mb-2" />
                <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{s.label}</p>
                <p className="font-display font-bold text-base capitalize">{s.val}</p>
              </div>
            ))}
          </div>

          <div className="rise rise-3">
            <h2 className="font-display text-2xl font-bold mb-3">About this car</h2>
            <p className="text-slate-300 leading-relaxed">{car.description}</p>
          </div>

          {car.features?.length > 0 && (
            <div className="rise rise-3">
              <h2 className="font-display text-2xl font-bold mb-3">Features</h2>
              <div className="flex flex-wrap gap-2">
                {car.features.map((f) => <Badge key={f} className="bg-slate-800 text-slate-200 border-slate-700" data-testid={`feature-${f}`}>{f}</Badge>)}
              </div>
            </div>
          )}

          {user && (
            <div className="rise rise-3">
              <h2 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Required documents</h2>
              <p className="text-sm text-slate-400 mb-4">Upload driver's license and insurance before booking. We auto-flag mismatches.</p>
              <div className="space-y-3">
                <DocumentUploader kind="license" label="Driver's License" hint="Photo or scan (jpg, png, pdf · max 10 MB)" value={licensePath} onChange={setLicensePath} />
                <DocumentUploader kind="insurance" label="Proof of Insurance" hint="Current declarations page or card" value={insurancePath} onChange={setInsurancePath} />
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-28 h-fit rise rise-2">
          <div className="glass rounded-3xl p-6">
            <div className="flex items-baseline justify-between mb-5">
              <span className="font-display text-4xl font-extrabold">${car.daily_price}</span>
              <span className="text-sm text-slate-400 font-mono uppercase tracking-widest">/ day</span>
            </div>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1 block">Pickup</label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="booking-start-date" className="bg-slate-900 border-slate-800" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1 block">Return</label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="booking-end-date" className="bg-slate-900 border-slate-800" />
              </div>
            </div>
            {days > 0 && (
              <div className="space-y-2 py-4 border-t border-slate-800 text-sm">
                <div className="flex justify-between text-slate-400"><span>${car.daily_price} × {days} days</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-400"><span>Service fee</span><span className="text-white">${fee.toFixed(2)}</span></div>
                {credit > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="credit-toggle-row">
                    <div><p className="text-emerald-300 text-sm font-semibold">Apply ${credit.toFixed(2)} credit</p><p className="text-[11px] text-slate-400">Refund credit from your account</p></div>
                    <Switch checked={applyCredit} onCheckedChange={setApplyCredit} data-testid="apply-credit-toggle" />
                  </div>
                )}
                {creditUsed > 0 && <div className="flex justify-between text-emerald-400"><span>Credit applied</span><span>−${creditUsed.toFixed(2)}</span></div>}
                {kitPrice > 0 && <div className="flex justify-between text-cyan-300" data-testid="kit-line"><span>{kit.name}</span><span>+${kitPrice.toFixed(2)}</span></div>}
                <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-slate-800"><span>{creditUsed > 0 ? "Due now" : "Total"}</span><span className="text-emerald-400" data-testid="booking-total">${due.toFixed(2)}</span></div>
                {days > 0 && <FinanceHint amount={due} context="booking" testId="finance-booking" />}
              </div>
            )}
            {user && days > 0 && <KitUpsell user={user} enabled={kitOn} onToggle={(v, k) => { setKit(k); setKitOn(v); }} shipping={kitShip} onShipping={setKitShip} />}
            <Button onClick={book} disabled={booking || days <= 0} data-testid="book-now-btn"
              className="w-full h-12 mt-4 bg-white text-slate-950 hover:bg-slate-100 font-semibold btn-glow">
              {booking ? "Processing…" : days > 0 ? (due <= 0 ? "Book with credit — $0.00" : `Book — $${due.toFixed(2)}`) : "Pick dates to book"}
            </Button>
            {user && (!licensePath || !insurancePath) && days > 0 && (
              <p className="text-xs text-amber-400 text-center mt-2">Tip: upload docs to speed up pickup.</p>
            )}
            <p className="text-xs text-slate-500 text-center mt-3" data-testid="payment-methods-hint">You won't be charged until the next step · Pay with card, <span className="text-pink-300">Klarna</span> or <span className="text-sky-300">Affirm</span>.</p>
            <div className="mt-4"><LocalLeadForm compact carId={car.car_id} city={car.city} title="Ask the local team about this car" /></div>
          </div>
          <div className="mt-4"><AutoCareCta variant="inline" /></div>
        </aside>
      </div>
    </main>
  );
}
